import { z } from 'zod';
// ---------------------------------------------------------------------------
// Publicación de contenido (ciclo-panel, R2/R4).
//
// Es el contrato que lee `Metamorfosis - MAESTRO` (n8n), antes en la base de Notion
// `📅 Contenido Orgánico`. Los valores con emoji son los NOMBRES EXACTOS de
// las opciones de la base: si se cambian acá sin cambiarlos allá, el flujo
// deja de reconocerlos. La validación reproduce las reglas del nodo
// "Filtrar por fecha modo" de MAESTRO para que nada cargado desde la app
// termine marcado `Error`.
// ---------------------------------------------------------------------------
export const FORMATOS_PUBLICACION = ['REEL', 'CARRUSEL', 'HISTORIA'];
export const FORMATO_NOTION = {
    REEL: '🎬 Reel',
    CARRUSEL: '🖼️ Carrusel',
    HISTORIA: '📖 Historia',
};
export const REDES_PUBLICACION = ['INSTAGRAM', 'TIKTOK', 'YOUTUBE', 'FACEBOOK'];
export const RED_NOTION = {
    INSTAGRAM: '📸 Instagram',
    TIKTOK: '🎵 TikTok',
    YOUTUBE: '▶️ YouTube',
    FACEBOOK: '👥 Facebook',
};
export const PILARES_PUBLICACION = ['ALCANCE', 'POSICIONAMIENTO', 'CONVERSION'];
export const PILAR_NOTION = {
    ALCANCE: '🟢 Alcance',
    POSICIONAMIENTO: '🔵 Posicionamiento',
    CONVERSION: '🔴 Conversión',
};
export const MODOS_PUBLICACION = ['PROGRAMAR', 'PUBLICAR_YA'];
export const MODO_NOTION = {
    PROGRAMAR: 'Programar',
    PUBLICAR_YA: 'Publicar ya',
};
// El select "Hora de publicación" de la base: de 08:00 a 22:00, en punto.
export const HORAS_PUBLICACION = Array.from({ length: 15 }, (_, i) => `${String(i + 8).padStart(2, '0')}:00`);
export const HORA_PUBLICACION_DEFAULT = '13:00';
// Reglas de MAESTRO: zona fija, 24 h de gracia hacia atrás, 5 días de horizonte.
export const PUBLICACION_TZ_OFFSET = '-03:00';
export const PUBLICACION_GRACIA_MS = 24 * 60 * 60 * 1000;
export const PUBLICACION_HORIZONTE_DIAS = 5;
// Tope del flujo: MAESTRO sube el video a Cloudinary con MAX_SIZE = 95 MB.
export const PUBLICACION_ARCHIVO_MAX_BYTES = 95 * 1024 * 1024;
export const ESTADOS_PUBLICACION = ['Borrador', 'Pendiente', 'Programado', 'Publicado', 'Error'];
const fechaIso = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Fecha inválida (AAAA-MM-DD)')
    .refine((f) => !Number.isNaN(new Date(`${f}T00:00:00Z`).getTime()), 'Fecha inválida');
/** Instante de publicación tal como lo calcula MAESTRO (fecha + hora, -03:00). */
export function instantePublicacion(fecha, hora) {
    return new Date(`${fecha}T${hora}:00${PUBLICACION_TZ_OFFSET}`);
}
/** true si MAESTRO la va a saltar por ahora (más de 5 días adelante). */
export function fueraDeHorizonte(fecha, hora, ahora = new Date()) {
    const t = instantePublicacion(fecha, hora).getTime();
    return t > ahora.getTime() + PUBLICACION_HORIZONTE_DIAS * 24 * 60 * 60 * 1000;
}
// Los campos comunes; los archivos viajan aparte (multipart) y los valida el backend.
const base = z.object({
    titulo: z.string().trim().min(3, 'El título necesita al menos 3 letras').max(120),
    caption: z.string().trim().max(2200, 'Instagram corta en 2.200 caracteres').default(''),
    hashtags: z.string().trim().max(500).default(''),
    formato: z.enum(FORMATOS_PUBLICACION),
    redes: z.array(z.enum(REDES_PUBLICACION)).min(1, 'Elegí al menos una red'),
    pilar: z.enum(PILARES_PUBLICACION).nullable().optional(),
    modo: z.enum(MODOS_PUBLICACION),
    fecha: fechaIso.nullable().optional(),
    hora: z
        .string()
        .refine((h) => HORAS_PUBLICACION.includes(h), 'La hora tiene que ser en punto, entre 08:00 y 22:00')
        .default(HORA_PUBLICACION_DEFAULT),
    notas: z.string().trim().max(1000).default(''),
});
/**
 * Validación de MAESTRO reproducida: si programa, necesita fecha y esa fecha+hora
 * no puede estar más de 24 h en el pasado. `ahora` se inyecta para los tests.
 */
export function publicacionCrearSchemaCon(ahora = () => new Date()) {
    return base.superRefine((v, ctx) => {
        if (v.modo !== 'PROGRAMAR')
            return;
        if (!v.fecha) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['fecha'],
                message: 'Para programar hace falta la fecha',
            });
            return;
        }
        const t = instantePublicacion(v.fecha, v.hora).getTime();
        if (Number.isNaN(t)) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['fecha'], message: 'Fecha u hora inválida' });
            return;
        }
        if (t < ahora().getTime() - PUBLICACION_GRACIA_MS) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['fecha'], message: 'Esa fecha y hora ya pasaron' });
        }
    });
}
export const publicacionCrearSchema = publicacionCrearSchemaCon();
// Lo que devuelve POST /publicaciones.
export const publicacionCreadaSchema = z.object({
    // ciclo-contenido-sin-notion: el id de la publicación en la app (antes, la página de Notion).
    id: z.string(),
    nombreArchivo: z.string(),
    fecha: z.string().nullable(),
    hora: z.string(),
    modo: z.enum(MODOS_PUBLICACION),
    redes: z.array(z.enum(REDES_PUBLICACION)),
    fueraDeHorizonte: z.boolean(),
});
// GET /publicaciones: sin rango, las últimas N; con rango (AAAA-MM-DD), las de ese
// período por Fecha de publicación — es lo que usa el calendario del Publicador.
export const publicacionesQuerySchema = z.object({
    desde: fechaIso.optional(),
    hasta: fechaIso.optional(),
    limite: z.coerce.number().int().min(1).max(100).default(30),
});
// Una fila de la lista (GET /publicaciones). Desde la fase 7 viene de la base de la app.
export const publicacionPublicSchema = z.object({
    id: z.string(),
    titulo: z.string(),
    estado: z.string(),
    formato: z.string().nullable(),
    redes: z.array(z.string()),
    fecha: z.string().nullable(),
    hora: z.string().nullable(),
    modo: z.string().nullable(),
    nombreArchivo: z.string(),
    fechaReal: z.string().nullable(),
});
/**
 * Nombre de archivo que genera la app (R2): `AAAA-MM-DD_titulo-normalizado`.
 * Sin la extensión: la agrega el backend según el archivo real.
 */
export function nombreBasePublicacion(titulo, fecha) {
    const slug = titulo
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 60)
        .replace(/-+$/g, '');
    return `${fecha}_${slug || 'publicacion'}`;
}
//# sourceMappingURL=publicacion.js.map